import { useState, useRef, useEffect } from "react";
import moment from "moment";
import { useRecorder } from "../../hooks/useRecorder.js";
import { useChats } from "../../hooks/useChats.js";
import { useAuth } from "../../hooks/useAuth.js";
import { useTranslation } from "react-i18next";

export default function RecorderBar() {
  const {
    recording,
    stopRecording,
    time,
    ruralMode,
    quickstart,
    chatIdRef,
    devices,
    setCurrentDevice,
    currentDevice,
    volumeLevel,
    showNoAudioWarning,
    canStopRecording,
  } = useRecorder();
  const { isMobileApp } = useAuth();
  const { t } = useTranslation();
  const { generate, setAutogenerate } = useChats();
  const [showDevicePicker, setShowDevicePicker] = useState(false);
  const devicePickerRef = useRef(null);

  useEffect(() => {
    if (!showDevicePicker) return;
    const handleClickOutside = (e) => {
      if (devicePickerRef.current && !devicePickerRef.current.contains(e.target)) {
        setShowDevicePicker(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowDevicePicker(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showDevicePicker]);

  const generateNote = async () => {
    if (recording) {
      setAutogenerate(true, chatIdRef.current);
      return stopRecording();
    }
    generate(chatIdRef.current);
  };

  const formattedTime = moment
    .utc(moment.duration(time, "seconds").as("milliseconds"))
    .format(time >= 3600 ? "HH:mm:ss" : "mm:ss");

  return (
    <>
      {recording && (
        <div className="recording-bar">
          <div className="recording-label z-index-2">
            <div className="recording-label-content">
              {ruralMode ? (
                <>
                  <div className="recording-label-row">
                    <i className="notranslate material-icons">radio_button_checked</i>
                    {formattedTime}
                  </div>
                  <div className="badge-rural">{t("layout.recorder.ruralMode")}</div>
                </>
              ) : (
                <>
                  <i className="notranslate material-icons">radio_button_checked</i>
                  {formattedTime}
                </>
              )}
            </div>

            <div className="volume-indicator">
              <div className="volume-bars">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className={`volume-bar ${volumeLevel > (i + 1) * 10 ? "active" : ""}`} />
                ))}
              </div>
            </div>

            {devices.length > 1 && !isMobileApp && (
              <div className="device-picker-wrapper" ref={devicePickerRef}>
                <button
                  className="device-picker-toggle"
                  onClick={() => setShowDevicePicker((v) => !v)}
                  aria-expanded={showDevicePicker}
                  aria-haspopup="listbox"
                >
                  <i className="notranslate material-icons">mic</i>
                  <i className="notranslate material-icons device-picker-chevron">expand_more</i>
                </button>
                {showDevicePicker && (
                  <div className="device-picker-popover" role="listbox">
                    {devices.map((device) => (
                      <button
                        key={device.deviceId}
                        role="option"
                        aria-selected={currentDevice === device.deviceId}
                        className={"device-picker-option" + (currentDevice === device.deviceId ? " active" : "")}
                        onClick={() => {
                          setCurrentDevice(device.deviceId);
                          setShowDevicePicker(false);
                        }}
                      >
                        <i className="notranslate material-icons">{currentDevice === device.deviceId ? "mic" : "mic_none"}</i>
                        <span>{device.label || t("layout.recorder.microphone", { id: device.deviceId })}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {showNoAudioWarning && (
            <div className="no-audio-warning">
              <span className="no-audio-dot"></span>
              <i className="notranslate material-icons">mic_off</i>
              <span className="no-audio-text">{t("layout.recorder.noAudioWarning")}</span>
            </div>
          )}

          <div className="record-btn-recording-bg mobile"></div>
          <button className="white btn-recording hide-desktop" onClick={stopRecording} disabled={!canStopRecording}>
            {t("layout.recorder.stop")}
          </button>

          <div className="bar-right">
            {!quickstart && (
              <button className="white mobile z-index-1" onClick={generateNote} disabled={!canStopRecording}>
                {t("layout.recorder.generate")}
              </button>
            )}
            <button className="white desktop btn-stop" onClick={stopRecording} disabled={!canStopRecording}>
              {t("layout.recorder.stop")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
